import type {
  AssistantInsightsFilters,
  InsightsBreakdown,
  InsightsBreakdownApiResource,
  InsightsDailyPoint,
  InsightsDailyPointApiResource,
  InsightsSummary,
  InsightsSummaryApiResource,
  InsightsWorkspace,
  InsightsWorkspaceApiResource,
} from './assistantInsightsApi.types';

export function buildInsightsQueryParameters(
  filters: AssistantInsightsFilters
): Record<string, string> {
  const parameters: Record<string, string> = {
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
  };
  if (filters.tenantId !== null) {
    parameters.tenant_id = filters.tenantId;
  }
  if (filters.topic !== null) {
    parameters.topic = filters.topic;
  }
  if (filters.screen !== null) {
    parameters.screen = filters.screen;
  }
  return parameters;
}

function mapBreakdown(resource: InsightsBreakdownApiResource): InsightsBreakdown {
  return {
    key: resource.key,
    costMicroUsd: resource.cost_micro_usd,
    questions: resource.questions,
    tokens: resource.tokens,
  };
}

function mapDailyPoint(resource: InsightsDailyPointApiResource): InsightsDailyPoint {
  return {
    date: resource.date,
    costMicroUsd: resource.cost_micro_usd,
    questions: resource.questions,
    tokens: resource.tokens,
    costByTopic: resource.cost_by_topic,
    tokensByTopic: resource.tokens_by_topic,
  };
}

export function mapApiResourceToInsightsSummary(
  resource: InsightsSummaryApiResource
): InsightsSummary {
  const totals = resource.totals;
  return {
    dateFrom: resource.date_from,
    dateTo: resource.date_to,
    totals: {
      costMicroUsd: totals.cost_micro_usd,
      projectedCostMicroUsd: totals.projected_cost_micro_usd,
      costPerQuestionMicroUsd: totals.cost_per_question_micro_usd,
      questions: totals.questions,
      modelCalls: totals.model_calls,
      activeUsers: totals.active_users,
      activeWorkspaces: totals.active_workspaces,
      inputTokens: totals.input_tokens,
      cachedInputTokens: totals.cached_input_tokens,
      outputTokens: totals.output_tokens,
      cacheRate: totals.cache_rate,
    },
    daily: resource.daily.map(mapDailyPoint),
    byTopic: resource.by_topic.map(mapBreakdown),
    byScreen: resource.by_screen.map(mapBreakdown),
    byModel: resource.by_model.map(function mapModel(model) {
      return {
        provider: model.provider,
        model: model.model,
        costMicroUsd: model.cost_micro_usd,
        modelCalls: model.model_calls,
      };
    }),
    rateCard: {
      inputUsdPerMillion: resource.rate_card.input_usd_per_million,
      cachedInputUsdPerMillion: resource.rate_card.cached_input_usd_per_million,
      cacheWriteUsdPerMillion: resource.rate_card.cache_write_usd_per_million,
      outputUsdPerMillion: resource.rate_card.output_usd_per_million,
    },
    monthlyBudgetTokens: resource.monthly_budget_tokens,
  };
}

export function mapApiResourceToInsightsWorkspace(
  resource: InsightsWorkspaceApiResource
): InsightsWorkspace {
  return {
    tenantId: resource.tenant_id,
    tenantName: resource.tenant_name,
    costMicroUsd: resource.cost_micro_usd,
    costPerQuestionMicroUsd: resource.cost_per_question_micro_usd,
    questions: resource.questions,
    activeUsers: resource.active_users,
    tokens: resource.input_tokens + resource.output_tokens,
    cacheRate: resource.cache_rate,
    budgetUsedThisMonth: resource.budget_used_this_month,
  };
}
